import React, { useEffect, useMemo, useState } from 'react';
import {
  FiBriefcase,
  FiCopy,
  FiExternalLink,
  FiMail,
  FiPause,
  FiPhone,
  FiPlay,
  FiPlus,
  FiSearch,
  FiUserCheck,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/fr';
import { useForm } from 'react-hook-form';

import {
  Badge,
  Button,
  DataTable,
  Drawer,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  StatusPill,
  Toolbar,
  cn,
  toast,
} from '../components/ui';
import Modal from '../components/Modal';
import {
  createPartner,
  getAllPartners,
  updatePartnerStatus,
} from '../servicesV2/partnerService';

/* -------------------------------------------------------------------------- */
/*  Create partner modal                                                      */
/* -------------------------------------------------------------------------- */

function AddPartnerModal({ close, onCreated }) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { max_clients: 50 } });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await createPartner({
        company_name: data.company_name,
        contact_email: data.contact_email || undefined,
        contact_phone: data.contact_phone || undefined,
        max_clients: data.max_clients ? parseInt(data.max_clients, 10) : 50,
        admin_name: data.admin_name,
        admin_email: data.admin_email,
        admin_password: data.admin_password,
      });
      toast.success('Partenaire créé', { description: data.company_name });
      onCreated?.();
      close();
    } catch (err) {
      console.error(err);
      toast.error('Échec de la création', { description: err?.message });
    } finally {
      setLoading(false);
    }
  };

  const req = t('GLOBAL.REQUIRED_ERROR', { defaultValue: 'Champ requis' });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <Field
        label={t('PARTNERS.FORM.COMPANY_NAME', { defaultValue: 'Nom de la société' })}
        required
        error={errors.company_name?.message}
      >
        <Input
          {...register('company_name', { required: req })}
          invalid={!!errors.company_name}
          placeholder="Acme Corp"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label={t('PARTNERS.FORM.CONTACT_EMAIL', { defaultValue: 'Email contact' })}>
          <Input type="email" {...register('contact_email')} placeholder="contact@acme.com" />
        </Field>
        <Field label={t('PARTNERS.FORM.CONTACT_PHONE', { defaultValue: 'Téléphone' })}>
          <Input {...register('contact_phone')} placeholder="+33 1 23 45 67 89" />
        </Field>
      </div>

      <Field
        label={t('PARTNERS.FORM.MAX_CLIENTS', { defaultValue: 'Quota clients' })}
        hint="Nombre maximum de tenants que ce partenaire peut provisionner."
      >
        <Input type="number" min={1} {...register('max_clients')} />
      </Field>

      <div className="mt-2 rounded-lg border border-line bg-surface-muted p-4">
        <p className="mb-3 text-body-sm font-semibold text-fg">
          {t('PARTNERS.FORM.ADMIN_SECTION', { defaultValue: 'Administrateur' })}
        </p>
        <div className="flex flex-col gap-3">
          <Field
            label={t('PARTNERS.FORM.ADMIN_NAME', { defaultValue: 'Nom' })}
            required
            error={errors.admin_name?.message}
          >
            <Input
              {...register('admin_name', { required: req })}
              invalid={!!errors.admin_name}
            />
          </Field>
          <Field
            label={t('PARTNERS.FORM.ADMIN_EMAIL', { defaultValue: 'Email' })}
            required
            error={errors.admin_email?.message}
          >
            <Input
              type="email"
              {...register('admin_email', { required: req })}
              invalid={!!errors.admin_email}
            />
          </Field>
          <Field
            label={t('PARTNERS.FORM.ADMIN_PASSWORD', { defaultValue: 'Mot de passe' })}
            required
            error={errors.admin_password?.message}
            hint="Minimum 8 caractères."
          >
            <Input
              type="password"
              {...register('admin_password', {
                required: req,
                minLength: { value: 8, message: t('ERRORS.PASSWORD_MIN_LENGTH', { defaultValue: '8 caractères minimum' }) },
              })}
              invalid={!!errors.admin_password}
            />
          </Field>
        </div>
      </div>

      <div className="mt-2 flex items-center justify-end gap-2 border-t border-line pt-4">
        <Button variant="secondary" type="button" onClick={close}>
          {t('GLOBAL.CANCEL', { defaultValue: 'Annuler' })}
        </Button>
        <Button type="submit" loading={loading}>
          {t('GLOBAL.SAVE', { defaultValue: 'Créer' })}
        </Button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/*  Drawer peek                                                               */
/* -------------------------------------------------------------------------- */

function QuotaBar({ used, max }) {
  const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const tone =
    pct >= 90 ? 'bg-danger-500' : pct >= 70 ? 'bg-warning-500' : 'bg-brand-500';
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <p className="text-body-sm text-fg">
          {used} <span className="text-fg-subtle">/ {max}</span>
        </p>
        <p className="text-caption text-fg-subtle">{pct}% utilisé</p>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-ink-100">
        <div className={cn('h-full rounded-full transition-all', tone)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function PartnerDrawer({ partner, isOpen, close, onStatusChange }) {
  const [busy, setBusy] = React.useState(false);
  if (!partner) return null;
  const used = partner.client_count ?? 0;
  const max = partner.max_clients ?? 50;

  const canSuspend = partner.status === 'active';
  const canActivate = partner.status === 'suspended' || partner.status === 'inactive';

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    toast.success('Copied', { description: text });
  };

  const toggleStatus = async (next) => {
    setBusy(true);
    try {
      await updatePartnerStatus(partner.id, next);
      toast.success(`Partenaire ${next === 'active' ? 'activé' : 'suspendu'}`);
      onStatusChange?.();
    } catch (err) {
      console.error(err);
      toast.error('Échec', { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      close={close}
      title={partner.company_name}
      subtitle={partner.contact_email}
      headerActions={
        <Button
          as={Link}
          to={`/partners/${partner.id}`}
          variant="secondary"
          size="sm"
          iconLeft={<FiExternalLink size={14} />}
          onClick={close}
        >
          Gérer
        </Button>
      }
    >
      <Drawer.Body className="space-y-5">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-brand-100 text-brand-700">
            <FiBriefcase size={20} />
          </div>
          <div className="min-w-0">
            <p className="text-h2 text-fg truncate">{partner.company_name}</p>
            <div className="mt-1 flex items-center gap-2">
              <StatusPill status={partner.status} />
              <span className="text-caption text-fg-subtle">
                créé le {moment(partner.created_at).format('DD MMM YYYY')}
              </span>
            </div>
          </div>
        </div>

        <section>
          <h3 className="mb-2 text-micro text-fg-subtle">Contact</h3>
          <div className="rounded-lg border border-line bg-surface-muted divide-y divide-line">
            {partner.contact_email && (
              <ContactRow
                icon={<FiMail size={12} />}
                label="Email"
                value={partner.contact_email}
                onCopy={() => copy(partner.contact_email)}
              />
            )}
            {partner.contact_phone && (
              <ContactRow
                icon={<FiPhone size={12} />}
                label="Téléphone"
                value={partner.contact_phone}
                onCopy={() => copy(partner.contact_phone)}
              />
            )}
            {!partner.contact_email && !partner.contact_phone && (
              <p className="px-4 py-3 text-caption text-fg-subtle">
                Aucun contact renseigné.
              </p>
            )}
          </div>
        </section>

        <section>
          <h3 className="mb-2 text-micro text-fg-subtle">Quota clients</h3>
          <div className="rounded-lg border border-line bg-surface-muted p-4">
            <QuotaBar used={used} max={max} />
          </div>
        </section>
      </Drawer.Body>

      <Drawer.Footer>
        {canSuspend && (
          <Button
            variant="danger-ghost"
            iconLeft={<FiPause size={14} />}
            loading={busy}
            onClick={() => toggleStatus('suspended')}
          >
            Suspendre
          </Button>
        )}
        {canActivate && (
          <Button
            variant="secondary"
            iconLeft={<FiPlay size={14} />}
            loading={busy}
            onClick={() => toggleStatus('active')}
          >
            Réactiver
          </Button>
        )}
        <Button
          as={Link}
          to={`/partners/${partner.id}`}
          variant="primary"
          iconLeft={<FiExternalLink size={14} />}
          onClick={close}
        >
          Page de gestion
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
}

function ContactRow({ icon, label, value, onCopy }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <div className="min-w-0">
        <p className="text-caption text-fg-subtle">{label}</p>
        <p className="mt-0.5 inline-flex items-center gap-1.5 truncate text-body-sm text-fg">
          <span className="text-fg-subtle">{icon}</span>
          {value}
        </p>
      </div>
      <button
        type="button"
        onClick={onCopy}
        className="shrink-0 rounded p-1.5 text-fg-muted transition-colors hover:bg-ink-100 hover:text-fg"
        title="Copy"
      >
        <FiCopy size={13} />
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

function Partners() {
  const { t } = useTranslation();

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const res = await getAllPartners();
      setPartners(Array.isArray(res) ? res : res?.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Chargement impossible', { description: err?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return partners.filter((p) => {
      const matchQ =
        !q ||
        [p.company_name, p.contact_email, p.contact_phone]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      const matchS = !statusFilter || p.status === statusFilter;
      return matchQ && matchS;
    });
  }, [partners, query, statusFilter]);

  const isFiltered = query.length > 0 || statusFilter.length > 0;

  const openDrawer = (p) => {
    setSelected(p);
    setDrawerOpen(true);
  };

  const columns = [
    {
      id: 'company',
      header: t('PARTNERS.TABLE.COMPANY_NAME', { defaultValue: 'Partenaire' }),
      accessor: 'company_name',
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-ink-100 text-fg-muted">
            <FiBriefcase size={14} />
          </span>
          <div className="min-w-0">
            <div className="truncate text-body-sm font-medium text-fg">
              {row.company_name}
            </div>
            {row.contact_email && (
              <div className="truncate text-caption text-fg-subtle">
                {row.contact_email}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'status',
      header: t('PARTNERS.TABLE.STATUS', { defaultValue: 'Statut' }),
      accessor: 'status',
      sortable: true,
      cell: (row) => <StatusPill status={row.status} />,
    },
    {
      id: 'quota',
      header: t('PARTNERS.TABLE.CLIENT_COUNT', { defaultValue: 'Clients' }),
      accessor: (row) => row.client_count ?? 0,
      sortable: true,
      cell: (row) => {
        const used = row.client_count ?? 0;
        const max = row.max_clients ?? 50;
        const pct = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
        return (
          <div className="flex items-center gap-2 min-w-[140px]">
            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-ink-100">
              <div
                className={cn(
                  'h-full rounded-full',
                  pct >= 90
                    ? 'bg-danger-500'
                    : pct >= 70
                      ? 'bg-warning-500'
                      : 'bg-brand-500',
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-fg font-mono text-caption">
              {used}
              <span className="text-fg-subtle"> / {max}</span>
            </span>
          </div>
        );
      },
    },
    {
      id: 'created',
      header: t('PARTNERS.TABLE.CREATED_AT', { defaultValue: 'Créé le' }),
      accessor: 'created_at',
      sortable: true,
      cell: (row) => (
        <span className="text-fg-muted">
          {row.created_at ? moment(row.created_at).format('DD MMM YYYY') : '—'}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('PARTNERS.TITLE', { defaultValue: 'Partners' })}
        count={partners.length}
        description="Intégrateurs et revendeurs qui provisionnent leurs propres clients."
        actions={
          <Button iconLeft={<FiPlus size={14} />} onClick={() => setAddOpen(true)}>
            {t('PARTNERS.ADD_BTN', { defaultValue: 'Nouveau partenaire' })}
          </Button>
        }
      />

      <Toolbar
        left={
          <>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher par nom, email…"
              leading={<FiSearch size={13} />}
              containerClassName="max-w-sm w-full"
            />
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="max-w-[180px]"
            >
              <option value="">{t('PARTNERS.FILTERS.ALL', { defaultValue: 'Tous les statuts' })}</option>
              <option value="active">{t('PARTNERS.FILTERS.ACTIVE', { defaultValue: 'Actif' })}</option>
              <option value="suspended">{t('PARTNERS.FILTERS.SUSPENDED', { defaultValue: 'Suspendu' })}</option>
              <option value="inactive">{t('PARTNERS.FILTERS.INACTIVE', { defaultValue: 'Inactif' })}</option>
            </Select>
            {isFiltered && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setQuery('');
                  setStatusFilter('');
                }}
              >
                Réinitialiser
              </Button>
            )}
          </>
        }
        right={
          <Badge variant="neutral">
            {filtered.length}
            {filtered.length !== partners.length && (
              <span className="text-fg-subtle"> / {partners.length}</span>
            )}
          </Badge>
        }
      />

      <DataTable
        columns={columns}
        data={filtered}
        rowKey="id"
        onRowClick={openDrawer}
        loading={loading}
        isFiltered={isFiltered}
        initialSort={{ columnId: 'company', direction: 'asc' }}
        pageSize={25}
        empty={
          <EmptyState
            icon={<FiUserCheck size={22} />}
            title="Aucun partenaire"
            description="Ajoutez un intégrateur ou revendeur pour lui permettre de provisionner des clients."
            action={
              <Button iconLeft={<FiPlus size={14} />} onClick={() => setAddOpen(true)}>
                Nouveau partenaire
              </Button>
            }
          />
        }
        emptyFiltered={
          <EmptyState
            icon={<FiSearch size={22} />}
            title="Aucun résultat"
            description="Essayez d'assouplir les filtres."
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  setQuery('');
                  setStatusFilter('');
                }}
              >
                Réinitialiser
              </Button>
            }
          />
        }
      />

      <PartnerDrawer
        partner={selected}
        isOpen={drawerOpen}
        close={() => setDrawerOpen(false)}
        onStatusChange={() => {
          fetchPartners();
          setDrawerOpen(false);
        }}
      />

      <Modal
        isOpen={addOpen}
        close={() => setAddOpen(false)}
        title={t('PARTNERS.ADD_MODAL_TITLE', { defaultValue: 'Nouveau partenaire' })}
        size="lg"
      >
        <AddPartnerModal
          close={() => setAddOpen(false)}
          onCreated={() => {
            fetchPartners();
          }}
        />
      </Modal>
    </>
  );
}

export default Partners;
